import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2 } from 'lucide-react';
import { updateGroup, deleteGroup } from '@/lib/firebase/queries/groups';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { AVAILABLE_CATEGORIES, GROUP_CATEGORIES } from '@/lib/constants/groupCategories';
import type { Group, GroupCategory } from '@/types/support';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface EditGroupDialogProps {
  group: Group;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function EditGroupDialog({ group, open, onOpenChange, onSuccess }: EditGroupDialogProps) {
  const [name, setName] = useState(group.name);
  const [description, setDescription] = useState(group.description);
  const [isPrivate, setIsPrivate] = useState(group.isPrivate);
  const [selectedCategories, setSelectedCategories] = useState<GroupCategory[]>(group.categories || []);
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { t } = useLanguage();
  const { currentUser } = useAuth();

  useEffect(() => {
    if (open) {
      setName(group.name);
      setDescription(group.description);
      setIsPrivate(group.isPrivate);
      setSelectedCategories(group.categories || []);
    }
  }, [open, group]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !description.trim() || selectedCategories.length === 0) return;

    try {
      setIsLoading(true);
      const success = await updateGroup(group.id, {
        name: name.trim(),
        description: description.trim(),
        categories: selectedCategories,
        isPrivate,
        updatedAt: new Date()
      });

      if (success) {
        toast.success(t('support.groupUpdated'));
        onSuccess?.();
        onOpenChange(false);
      } else {
        toast.error(t('support.groupUpdateError'));
      }
    } catch (error) {
      console.error('Error updating group:', error);
      toast.error(t('support.groupUpdateError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!currentUser) return;

    try {
      setIsDeleting(true);
      const success = await deleteGroup(group.id, currentUser.uid);
      if (success) {
        toast.success(t('support.groupDeleted'));
        onSuccess?.();
        onOpenChange(false);
      } else {
        toast.error(t('support.groupDeleteError'));
      }
    } catch (error) {
      console.error('Error deleting group:', error);
      toast.error(t('support.groupDeleteError'));
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-screen h-screen sm:w-[90vw] sm:max-w-2xl sm:h-auto">
          <DialogHeader>
            <DialogTitle>{t('support.editGroup')}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">{t('support.groupName')}</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('support.groupNamePlaceholder')}
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">{t('support.groupDescription')}</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('support.groupDescriptionPlaceholder')}
                className="min-h-[100px]"
                disabled={isLoading}
              />
            </div>

            <div className="space-y-4">
              <Label>{t('support.groupCategories')}</Label>
              <div className="grid grid-cols-2 gap-4">
                {AVAILABLE_CATEGORIES.map((category) => (
                  <div key={category} className="flex items-center space-x-2">
                    <Checkbox
                      id={category}
                      checked={selectedCategories.includes(category)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedCategories(prev => [...prev, category]);
                        } else {
                          setSelectedCategories(prev => prev.filter(c => c !== category));
                        }
                      }}
                    />
                    <label
                      htmlFor={category}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      <span>{GROUP_CATEGORIES[category]}</span> {t(`support.categories.${category}`)}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="private">{t('support.isPrivate')}</Label>
                <p className="text-sm text-muted-foreground">
                  {t('support.isPrivateDescription')}
                </p>
              </div>
              <Switch
                id="private"
                checked={isPrivate}
                onCheckedChange={setIsPrivate}
                disabled={isLoading}
              />
            </div>

            <div className="flex justify-between">
              <Button
                type="button"
                variant="destructive"
                onClick={() => setShowDeleteDialog(true)}
                disabled={isLoading}
              >
                {t('support.deleteGroup')}
              </Button>

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={isLoading}
                >
                  {t('common.cancel')}
                </Button>
                <Button 
                  type="submit" 
                  disabled={isLoading || !name.trim() || !description.trim() || selectedCategories.length === 0}
                  className="bg-[#7dda55] hover:bg-[#6dc447] text-white"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('common.saving')}
                    </>
                  ) : (
                    t('common.saveChanges')
                  )}
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('support.deleteGroupTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('support.deleteGroupMessage')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.deleting')}
                </>
              ) : (
                t('support.deleteGroup')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}